import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';

import styles from '../../../styles/AddressStep.module.css';
import AddressSearchForm from './AddressSearchModal';
import { useZoningValidation } from '../../../hooks/useZoningValidation';
import { useSmartSend } from '../../../contexts/SmartSendContext';
import ErrorMessageWithSupport from '../../ErrorMessageWithSupport';
import {
  saveAddress,
  saveZoningData,
  clearAddress as clearStoredAddress,
  clearZoningData as clearStoredZoningData,
} from '../../../utils/localStorage';
import { ANALYTICS_SOURCE } from '../../../env/config';
import analytics from '../../../../../library/segmentAnalytics';

/**
 * AddressSelection Component - Manages address selection and validation
 *
 * Features:
 * - Displays greeting with recipient and sender names
 * - Address search modal integration
 * - Zoning validation with /zoning/v2 API
 * - Error handling for various zoning errors
 * - Local storage persistence
 * - Timezone detection
 *
 * @param {Object} props - Component props
 * @param {Object} props.order - Order data with sender/recipient info
 * @param {string} props.orderId - Order ID
 * @param {Object} props.address - Current address from context
 * @param {Function} props.onAddressValidated - Callback when address is validated successfully
 * @param {Function} props.onAddressChange - Callback to update address in context
 * @param {Function} props.onZoningDataChange - Callback to update zoning data in context
 * @param {Function} props.onCancelEdit - Callback when edit mode is cancelled
 */
const AddressSelection = ({
  order,
  orderId,
  address,
  onAddressValidated,
  onAddressChange,
  onZoningDataChange,
  onCancelEdit,
}) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [errorData, setErrorData] = useState(null);
  // Ref to track form reset counter - increments on error to force form remount
  // This ensures the form resets immediately when address is cleared after validation errors
  const formResetCounter = useRef(0);

  // Get phone number from context for support email
  const { phoneNumber } = useSmartSend();

  // Extract product ID from order data
  const productId = order?.productId || '';

  // Zoning validation mutation with React Query
  const zoningMutation = useZoningValidation({
    orderId,
    onSuccess: (zoningResponse, variables) => {
      // Success - store address and zoning data
      if (orderId) {
        saveAddress(orderId, variables.address);
        saveZoningData(orderId, zoningResponse.data);
      }

      // Update context
      onAddressChange(variables.address);
      onZoningDataChange(zoningResponse.data);

      // Notify parent that validation succeeded - this will hide AddressSelection
      onAddressValidated(variables.address, zoningResponse.data);
      toast.success('Address validated successfully!');
      setErrorMessage('');
      setErrorData(null);

      // Track address added (only if orderId and address exist)
      if (orderId && variables.address) {
        analytics.track('SmartSend Address Added', {
          source: ANALYTICS_SOURCE,
          orderId,
          address: variables.address,
        });
      }
    },
    onError: (error, variables) => {
      console.error('Zoning validation error:', error);

      // IMPORTANT: Store the attempted address BEFORE clearing it
      // This ensures we have the address data for the support email
      // Priority: variables.address (the address that was just attempted) > address prop (current address)
      const attemptedAddress = variables?.address || address || {};

      // Clear stored data on error
      if (orderId) {
        clearStoredAddress(orderId);
        clearStoredZoningData(orderId);
      } else {
        clearStoredAddress();
        clearStoredZoningData();
      }
      onAddressChange(null);
      onZoningDataChange(null);

      // Increment reset counter to force form remount when error occurs
      // This ensures the form fields reset immediately even if component doesn't re-render
      formResetCounter.current += 1;

      // Extract error code and message
      const errorCode = error.code;
      const backendMessage =
        error.response?.message?.message ||
        (error.response?.message && typeof error.response.message === 'string'
          ? error.response.message
          : null) ||
        (error.message && !error.message.includes('Zoning validation failed')
          ? error.message
          : null);

      // Build user-facing error message - ensure support email is always included
      let userMessage = '';

      if (backendMessage && typeof backendMessage === 'string') {
        // Use backend message, but ensure support email is appended if not present
        if (backendMessage.includes('support@gesture.vip')) {
          userMessage = backendMessage;
        } else {
          // Ensure proper punctuation before appending support text
          const trimmedMessage = backendMessage.trim();
          const endsWithPunctuation = /[.!?]$/.test(trimmedMessage);
          const separator = endsWithPunctuation ? ' ' : '. ';
          userMessage = `${trimmedMessage}${separator}Please contact support@gesture.vip if you need some help.`;
        }
      } else {
        // Fall back to frontend messages only if backend doesn't provide one
        switch (errorCode) {
          case 'COUNTRY_NOT_ALLOWED':
            userMessage =
              "This product can't be delivered to the selected country. Please contact support@gesture.vip if you need some help.";
            break;
          case 'DELIVERY_ZONE_NOT_ALLOWED':
            userMessage =
              "This product can't be delivered to the selected address. You can try a different address below, or contact support@gesture.vip if you need some help.";
            break;
          case 'SKU_EXPIRED':
            userMessage =
              'This product is no longer available for delivery. Please contact support@gesture.vip if you need some help.';
            break;
          default:
            userMessage =
              'Something went wrong. Please try again. Please contact support@gesture.vip if you need some help.';
        }
      }

      // Store error message and data for ErrorMessageWithSupport component
      // Include the attempted address (stored before clearing) and phone number from context
      setErrorMessage(userMessage);
      setErrorData({
        orderId: orderId || undefined,
        orderData: order || undefined,
        errorCode: errorCode || undefined,
        errorMessage: backendMessage || userMessage,
        phoneNumber: phoneNumber || '',
        address: attemptedAddress,
        context: 'Address validation error',
      });

      // Track address error (only if orderId exists)
      if (orderId) {
        analytics.track('SmartSend Address Error', {
          source: ANALYTICS_SOURCE,
          orderId,
          address: attemptedAddress || null,
          error: {
            code: errorCode || null,
            backendMessage: backendMessage || null,
            userMessage: userMessage || null,
          },
        });
      }
    },
  });

  /**
   * Handles address selection from form
   */
  const handleAddressSelect = (selectedAddress) => {
    setErrorMessage('');
    setErrorData(null);

    // Trigger zoning validation mutation
    zoningMutation.mutate({
      lat: selectedAddress.lat,
      lng: selectedAddress.lng,
      state: selectedAddress.state,
      country: selectedAddress.country,
      productId: productId,
      address: selectedAddress, // Pass address for declined location reporting
    });
  };

  /**
   * Handles form close/cancel
   */
  const handleFormClose = () => {
    // If in edit mode, call onCancelEdit to return to delivery form
    if (onCancelEdit) {
      onCancelEdit();
    }
  };

  return (
    <div
      className={styles.container}
      role="region"
      aria-label="Delivery address selection"
    >
      {/* Error Message - Displayed persistently (not toast) so user has time to read and click support */}
      {errorMessage && (
        <div className={styles.errorMessage} role="alert" aria-live="assertive">
          <ErrorMessageWithSupport
            message={errorMessage}
            errorData={errorData || {}}
          />
        </div>
      )}

      {/* Address Search Form - Always visible (inline card) */}
      <AddressSearchForm
        key={`address-form-${formResetCounter.current}`}
        isOpen={true}
        onClose={handleFormClose}
        onAddressSelect={handleAddressSelect}
        existingAddress={address}
        isValidating={zoningMutation.isLoading}
      />
    </div>
  );
};

export default AddressSelection;
